import { ReactNode } from 'react';
import TableHeader from './TableHeader';
import TableBody from './TableBody';
import {
  CheckboxDefinitionType,
  ColumnDefinitionType,
  MoreButtonDefinitionType,
  OptionsType
} from './genericTableTypes';
import * as Styled from './style';

type TableProps<T, K extends keyof T> = {
  id?: string;
  typeTutorial?: string;
  data: T[];
  theme?: string;
  columns: ColumnDefinitionType<T, K>[];
  moreButton?: MoreButtonDefinitionType<T>;
  equipmentSpecificOptions?: OptionsType[];
  additionalOptionsPosition?: number;
  shouldApplyOptions?: (equipType: string | undefined) => boolean;
  checkboxAll?: boolean
  checkbox?: CheckboxDefinitionType;
  onOrderTable: (columnName: K) => void;
  onRowClicked?: (id: number) => void;
  rowClickBehavior?: 'toggle-checkbox' | 'callback';
  expandedRows?: number[];
  renderExpandedRow?: (row: T) => ReactNode;
  showMobileHeaders?: boolean;
  isReording?: boolean;
  clickUpdate?: (id: number, typeOptions?: string) => void;
  clickType?: (type: string) => void;
  className?: string;
  onReorder?: (newOrder: any[]) => void;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  hasMore?: boolean;
  onEndReached?: () => void;
};

const GenericTable = <T extends { id: number }, K extends keyof T>({
  id,
  typeTutorial,
  data,
  columns,
  theme,
  moreButton = { show: false, options: [] },
  equipmentSpecificOptions = [],
  additionalOptionsPosition = 1,
  shouldApplyOptions = () => false,
  checkboxAll,
  checkbox = { show: false, action: () => { }, selectedList: [] },
  onOrderTable,
  onRowClicked = () => { },
  rowClickBehavior = 'toggle-checkbox',
  expandedRows,
  renderExpandedRow,
  showMobileHeaders = false,
  isReording = false,
  onReorder,
  clickUpdate,
  clickType,
  className,
  onScroll,
  hasMore = false,
  onEndReached
}: TableProps<T, K>) => {
  return (
    <Styled.Table id={id}>
      <TableHeader
        id={id}
        columns={columns}
        onFilterClick={onOrderTable}
        checkboxAll={checkboxAll}
        checkbox={checkbox}
        showCheckbox={checkbox.show}
        showMoreButton={moreButton.show}
        className={className}
      />
      <TableBody
        id={id}
        className={className}
        typeTutorial={typeTutorial}
        data={data}
        columns={columns}
        theme={theme}
        moreButton={moreButton}
        equipmentSpecificOptions={equipmentSpecificOptions}
        additionalOptionsPosition={additionalOptionsPosition}
        shouldApplyOptions={shouldApplyOptions}
        checkbox={checkbox}
        onRowClicked={onRowClicked}
        rowClickBehavior={rowClickBehavior}
        expandedRows={expandedRows}
        renderExpandedRow={renderExpandedRow}
        showMobileHeaders={showMobileHeaders}
        isReording={isReording}
        onReorder={onReorder}
        clickUpdate={clickUpdate}
        clickType={clickType}
        onScroll={onScroll}
        hasMore={hasMore}
        onEndReached={onEndReached}
      />
    </Styled.Table>
  );
};

export default GenericTable;
